/**
 * メインレイアウト (サイドバー + コンテンツ) - melta UI
 */
import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../services/userApi';
import { Icon } from './icons';

const NAV_ITEMS = [
  { path: '/',         icon: 'dashboard', label: 'ホーム',         shortLabel: 'Home' },
  { path: '/devices',  icon: 'device',    label: 'デバイス一覧',   shortLabel: 'Devices' },
  { path: '/groups',   icon: 'folder',    label: 'グループ管理',   shortLabel: 'Groups' },
  { path: '/provision',icon: 'plus',      label: 'デバイス登録',   shortLabel: 'New' },
  { path: '/firmware', icon: 'download',  label: 'ファームウェア', shortLabel: 'FW' },
  { path: '/firmware/devices', icon: 'activity', label: 'デバイスFW状況', shortLabel: 'FW状況' },
  { path: '/ota',      icon: 'upload',    label: 'OTA更新',        shortLabel: 'OTA' },
  { path: '/thing-versions', icon: 'tag', label: 'バージョン管理', shortLabel: 'Ver' },
  { path: '/flight-logs', icon: 'file',     label: 'フライトログ',   shortLabel: 'Logs' },
  { path: '/sitl-tests', icon: 'play',    label: 'SiTLテスト',     shortLabel: 'SiTL' },
  { path: '/hils-tests', icon: 'cpu',     label: 'HiLSテスト',     shortLabel: 'HiLS' },
  { path: '/pipeline', icon: 'git-branch', label: 'OTA CI/CD pipeline', shortLabel: 'OTA CI/CD' },
];

const ROLE_LABELS = {
  superadmin: 'システム管理者',
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    userApi.me().then(setProfile).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* サイドバー */}
      <aside
        className={`
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
          bg-white border-r border-slate-200 flex-shrink-0 flex flex-col
          transition-all duration-300
        `}
      >
        {/* Header */}
        <div className={`flex items-center border-b border-slate-200 ${sidebarCollapsed ? 'flex-col py-3 px-2' : 'gap-3 px-4 py-4'}`}>
          <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="wifi" className="w-5 h-5 text-white" />
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-slate-900 truncate">Device Management</h2>
              <span className="text-xs text-slate-500">社内向け管理コンソール</span>
            </div>
          )}
          <button
            className={`flex items-center justify-center rounded-lg text-slate-400 hover:bg-gray-100 hover:text-slate-600 transition-colors ${sidebarCollapsed ? 'w-full h-6 mt-2' : 'w-8 h-8'}`}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? 'サイドバーを展開' : 'サイドバーを折りたたむ'}
          >
            <Icon name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'} className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 py-4 ${sidebarCollapsed ? 'px-1' : 'px-3'} space-y-1`} aria-label="メインナビゲーション">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                sidebarCollapsed
                  ? `flex flex-col items-center justify-center py-2 rounded-lg text-xs font-medium transition-colors
                    ${isActive
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-body hover:bg-gray-50'
                    }`
                  : `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${isActive
                      ? 'bg-primary-50 text-primary-500'
                      : 'text-body hover:bg-gray-50'
                    }`
              }
              title={item.label}
              aria-current={({ isActive }) => isActive ? 'page' : undefined}
            >
              <Icon name={item.icon} className="w-5 h-5 flex-shrink-0" />
              {sidebarCollapsed ? (
                <span className="mt-1 text-[10px] leading-tight">{item.shortLabel}</span>
              ) : (
                <span className="truncate">{item.label}</span>
              )}
            </NavLink>
          ))}

          {/* superadmin 専用: システム管理 */}
          {profile?.role === 'superadmin' && (
            <>
              <div className={`my-2 ${sidebarCollapsed ? 'mx-2' : 'mx-0'} border-t border-slate-200`} />
              <NavLink
                to="/admin"
                className={({ isActive }) => {
                  const base = sidebarCollapsed
                    ? 'flex flex-col items-center justify-center py-2 rounded-lg text-xs font-medium transition-colors'
                    : 'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors';
                  const active = isActive ? 'bg-amber-50 text-amber-600' : 'text-amber-600 hover:bg-amber-50';
                  return `${base} ${active}`;
                }}
                title="システム管理"
              >
                <Icon name="shield" className="w-5 h-5 flex-shrink-0" />
                {sidebarCollapsed ? (
                  <span className="mt-1 text-[10px] leading-tight">Admin</span>
                ) : (
                  <span className="truncate">システム管理</span>
                )}
              </NavLink>
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="mt-auto border-t border-slate-200 p-3 space-y-2">
          {/* Organization info */}
          {!sidebarCollapsed && profile && (
            <div
              className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => navigate('/settings/organization')}
            >
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon
                  name={profile.orgType === 'organization' ? 'building' : 'user'}
                  className="w-4 h-4 text-slate-500"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {profile.org?.orgName || profile.displayName || 'マイアカウント'}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <span>{ROLE_LABELS[profile.role] || profile.role}</span>
                  <Icon name="settings" className="w-3 h-3" />
                </div>
              </div>
            </div>
          )}

          {/* Collapsed: settings link */}
          {sidebarCollapsed && (
            <NavLink
              to="/settings/organization"
              className="flex flex-col items-center justify-center w-full py-2 rounded-lg text-slate-500 hover:bg-gray-50 transition-colors"
              title="組織設定"
            >
              <Icon name="settings" className="w-5 h-5" />
              <span className="mt-1 text-[10px] leading-tight">Settings</span>
            </NavLink>
          )}

          {/* User info */}
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="user" className="w-4 h-4 text-primary-500" />
              </div>
              <span className="text-sm text-slate-600 truncate flex-1">
                {user?.email || 'User'}
              </span>
            </div>
          )}

          {/* Logout button */}
          <button
            className={`
              flex items-center w-full rounded-lg text-sm font-medium
              text-slate-600 hover:bg-gray-100 transition-colors
              ${sidebarCollapsed ? 'flex-col justify-center py-2' : 'gap-3 px-3 py-2'}
            `}
            onClick={handleLogout}
            title="ログアウト"
          >
            <Icon name="logout" className="w-5 h-5 flex-shrink-0" />
            {sidebarCollapsed ? (
              <span className="mt-1 text-[10px] leading-tight">Logout</span>
            ) : (
              <span>ログアウト</span>
            )}
          </button>
        </div>
      </aside>

      {/* メインコンテンツ */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
